// In src/components/ChurchEditModal.js

import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, Grid, IconButton,
  CircularProgress, FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

// A list of common IANA time zones for the dropdown
const timeZones = [
  "America/New_York",    // Eastern
  "America/Chicago",     // Central
  "America/Denver",      // Mountain
  "America/Phoenix",     // Mountain (no DST)
  "America/Los_Angeles", // Pacific
  "America/Anchorage",   // Alaska
  "Pacific/Honolulu",    // Hawaii
];

function ChurchEditModal({ open, onClose, onSave, church, onDeleteLogo, initialLogoUrl, editorProfile }) {
  const [churchData, setChurchData] = useState({
    name: '', address: '', city: '', state: '', zip: '', phone: '', email: '', website: '', logoUrl: '',
    timeZone: 'America/New_York', // Add new state with a default
    enableEventRegistration: false,
    enablePushNotifications: false,
    isPubliclyDiscoverable: false,
    autoJoinEnabled: false,
    enableEmailing: false,
    enableEventReminders: false,
    theme: {
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const isSuperAdmin = editorProfile?.role === 'Super Administrator';
  const isChurchAdmin = editorProfile?.role === 'Church Administrator';

  useEffect(() => {
    if (church) {
      setChurchData({
        ...church,
        timeZone: church.timeZone || 'America/New_York', // Load existing value
        enableEventRegistration: church.enableEventRegistration || false,
        enablePushNotifications: church.enablePushNotifications || false,
        isPubliclyDiscoverable: church.isPubliclyDiscoverable || false,
        autoJoinEnabled: church.autoJoinEnabled || false,
        enableEmailing: church.enableEmailing || false,
        enableEventReminders: church.enableEventReminders || false,
        theme: church.theme || { primaryColor: '#1976d2', secondaryColor: '#dc004e' },
      });
      setLogoPreviewUrl(church.logoUrl || '');
    } else {
      setChurchData({
          name: '', address: '', city: '', state: '', zip: '', phone: '', email: '', website: '', logoUrl: '',
          timeZone: 'America/New_York', // Default for new churches
          enableEventRegistration: false,
          enablePushNotifications: false,
          isPubliclyDiscoverable: false,
          autoJoinEnabled: false,
          enableEmailing: false,
          enableEventReminders: false,
          theme: { primaryColor: '#1976d2', secondaryColor: '#dc004e' },
      });
      setLogoPreviewUrl('');
    }
    setLogoFile(null);
  }, [church, open]);

  useEffect(() => {
    setLogoPreviewUrl(initialLogoUrl || '');
  }, [initialLogoUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setChurchData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleThemeChange = (e) => {
    const { name, value } = e.target;
    setChurchData(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [name]: value,
      }
    }));
  };

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setChurchData(prev => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    } else {
      setLogoFile(null);
      setLogoPreviewUrl(churchData.logoUrl || '');
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl('');
    setChurchData(prev => ({ ...prev, logoUrl: '' }));
    onDeleteLogo(church?.id, churchData.logoUrl);
  };

  const handleSave = async () => {
    setUploading(true);
    try {
      await onSave({ ...churchData, logoFile });
      setUploading(false);
      onClose();
    } catch (error) {
      console.error("Error saving church (modal):", error);
      setUploading(false);
      alert("Failed to save church: " + error.message);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6">{church ? 'Edit Church' : 'Create New Church'}</Typography>
        <TextField margin="normal" fullWidth label="Church Name" name="name" value={churchData.name} onChange={handleChange} required />
        <TextField margin="normal" fullWidth label="Street Address" name="address" value={churchData.address} onChange={handleChange} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}><TextField fullWidth label="City" name="city" value={churchData.city} onChange={handleChange} required /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth label="State" name="state" value={churchData.state} onChange={handleChange} required /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth label="Zip Code" name="zip" value={churchData.zip} onChange={handleChange} /></Grid>
        </Grid>
        <TextField margin="normal" fullWidth label="Phone Number" name="phone" value={churchData.phone} onChange={handleChange} />
        <TextField margin="normal" fullWidth label="Email Address" name="email" type="email" value={churchData.email} onChange={handleChange} />
        <TextField margin="normal" fullWidth label="Website (Optional)" name="website" value={churchData.website} onChange={handleChange} />

        {/* --- Start of Final Fix --- */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="timezone-select-label">Time Zone</InputLabel>
          <Select
            labelId="timezone-select-label"
            name="timeZone"
            value={churchData.timeZone}
            label="Time Zone"
            onChange={handleChange}
          >
            {timeZones.map((tz) => (
              <MenuItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* --- End of Final Fix --- */}

        <Box sx={{ mt: 2, border: '1px dashed grey', p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Theme Colors</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Primary Color (Hex Code)"
                        name="primaryColor"
                        value={churchData.theme.primaryColor}
                        onChange={handleThemeChange}
                        helperText="e.g., #1976d2"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Secondary Color (Hex Code)"
                        name="secondaryColor"
                        value={churchData.theme.secondaryColor}
                        onChange={handleThemeChange}
                        helperText="e.g., #dc004e"
                    />
                </Grid>
            </Grid>
        </Box>

        <Box sx={{ mt: 2, border: '1px dashed grey', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>Church Logo</Typography>
          {logoPreviewUrl ? (
            <Box sx={{ position: 'relative', mb: 2 }}>
              <img src={logoPreviewUrl} alt="Logo Preview" style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }} />
              <IconButton size="small" color="error" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'background.paper' }} onClick={handleRemoveLogo}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100', mb: 2 }}>
                <ImageNotSupportedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            </Box>
          )}

          <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploading}>
            {logoPreviewUrl ? "Change Logo" : "Upload Logo"}
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
          {uploading && <CircularProgress size={24} sx={{ mt: 1 }} />}
        </Box>

        {(isSuperAdmin || isChurchAdmin) && (
          <Box sx={{ mt: 2, border: '1px dashed grey', p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Member Onboarding</Typography>
            <FormControlLabel
              control={<Switch checked={churchData.isPubliclyDiscoverable} onChange={handleToggleChange} name="isPubliclyDiscoverable" />}
              label="Publicly Discoverable"
            />
            <FormControlLabel
              control={<Switch checked={churchData.autoJoinEnabled} onChange={handleToggleChange} name="autoJoinEnabled" />}
              label="Enable Auto-Join"
            />
          </Box>
        )}

        {isSuperAdmin && (
          <Box sx={{ mt: 2, border: '1px dashed grey', p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Core Feature Toggles</Typography>
            <FormControlLabel
              control={<Switch checked={churchData.enableEventRegistration} onChange={handleToggleChange} name="enableEventRegistration" />}
              label="Enable Event Registration"
            />
            <FormControlLabel
              control={<Switch checked={churchData.enablePushNotifications} onChange={handleToggleChange} name="enablePushNotifications" />}
              label="Enable Push Notifications"
            />
            <FormControlLabel
              control={<Switch checked={churchData.enableEmailing} onChange={handleToggleChange} name="enableEmailing" />}
              label="Enable Emailing Features"
            />
            <FormControlLabel
              control={<Switch checked={churchData.enableEventReminders} onChange={handleToggleChange} name="enableEventReminders" />}
              label="Enable Event Reminders"
            />
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 1 }} disabled={uploading}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={uploading}>Save</Button>
        </Box>
      </Box>
    </Modal>
  );
}
export default ChurchEditModal;